/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  Component,
  OnInit,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';
import { ArticleService } from '../../services/article.service';

interface ArticleHeaderData {
  title: string;
  description: string;
  coverBg: {
    small: string;
    large: string;
  };
}

interface User {
  current_user_permissions: {
    create_article: boolean;
  };
}

interface FeedStore {
  feedItems: any[];
  isLoading: boolean;
  isLastPage: boolean;
  load: () => void;
  loadMore: () => void;
}

@Component({
  selector: 'ut-articles',
  templateUrl: './articles.component.html',
  styleUrl: './articles.component.scss',
  standalone: false,
})
export class ArticlesComponent implements OnInit {
  headerData?: { data: ArticleHeaderData };
  coverBg?: string;

  currentUser: User | null = null;
  canCreateArticle = false;
  officialArticles?: FeedStore;
  contributedArticles?: FeedStore;
  popularArticles?: FeedStore;

  private readonly defaultHeaderData: ArticleHeaderData = {
    title: 'Software Testing Articles',
    description: 'Check out articles from professional testers -- on all topics related to software testing, QA, career advice, devices and more -- and write your own.',
    coverBg: {
      small: '/utest-static/assets/images/hero/articles-bg-xs.jpg',
      large: '/utest-static/assets/images/hero/articles-bg.jpg',
    },
  };

  constructor(
    private ArticleService: ArticleService,
    @Inject('UserService') private UserService: any,
    @Inject('FeedStore') private FeedStoreClass: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('StaticValuesService') private StaticValuesService: any,
    @Inject('ImageDataService') private ImageDataService: any,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadHeaderData();
    this.loadUser();
    this.initializeFeeds();
  }

  private loadHeaderData(): void {
    // Load header data from StaticValuesService
    this.StaticValuesService.pageData('articles').then(
      (response: any) => {
        // Merge response with defaults using lodash defaultsDeep
        const mergedData = this.deepDefaults(response.data || {}, this.defaultHeaderData);
        this.headerData = { data: mergedData };

        // Load cover background image
        this.loadCoverBg(mergedData);
      },
      (_error: any) => {
        // On error, use default data
        this.headerData = { data: this.defaultHeaderData };
        this.loadCoverBg(this.defaultHeaderData);
      },
    );
  }

  private loadCoverBg(headerData: ArticleHeaderData): void {
    const smallImagePath = headerData.coverBg.small;

    // If using default image, get it from ImageDataService, otherwise use as-is
    if (smallImagePath === '/utest-static/assets/images/hero/articles-bg-xs.jpg') {
      this.ImageDataService.getData(smallImagePath).then((dataUrl: string) => {
        this.coverBg = dataUrl;
      });
    } else {
      this.coverBg = smallImagePath;
    }
  }

  private deepDefaults(target: any, ...sources: any[]): any {
    // Simple implementation of lodash's defaultsDeep
    if (!target || typeof target !== 'object') {
      return target;
    }

    sources.forEach((source) => {
      if (source && typeof source === 'object') {
        Object.keys(source).forEach((key) => {
          if (target[key] === undefined) {
            target[key] = source[key];
          } else if (typeof target[key] === 'object' && typeof source[key] === 'object') {
            target[key] = this.deepDefaults(target[key], source[key]);
          }
        });
      }
    });

    return target;
  }

  private initializeFeeds(): void {
    // Create feed stores for each article type
    const fetchArticles = this.ArticleService.feed.bind(this.ArticleService);

    this.officialArticles = this.FeedStoreClass.create(fetchArticles, {
      per_page: 6,
      filter: { article_type: 'official' },
    });

    this.contributedArticles = this.FeedStoreClass.create(fetchArticles, {
      per_page: 6,
      filter: { article_type: 'community' },
    });

    this.popularArticles = this.FeedStoreClass.create(fetchArticles, {
      per_page: 6,
      sort: '-popularity',
      filter: {
        published_after: new Date(
          new Date().setFullYear(new Date().getFullYear() - 1),
        ).toISOString(),
      },
    });

    // Load initial data for all feeds
    this.officialArticles.load();
    this.contributedArticles.load();
    this.popularArticles.load();
  }

  loadUser(): void {
    this.UserService.me().then((res: any) => {
      if (res !== null) {
        this.currentUser = res.data;
        this.canCreateArticle = this.currentUser!.current_user_permissions.create_article;
      } else {
        this.canCreateArticle = false;
      }
      this.cdr.markForCheck();
    });
  }

  checkLightTester(event: Event): void {
    if (this.LightTesterErrorService.show()) {
      event.preventDefault();
    }
  }
}
